import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input } from '../components';
import OvoContractSign from '../components/OvoContractSign';
import { Briefcase, Video, Send, Trash2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchProfessionalProjecten,
  accepteerAanvraag,
  wijsAanvraagAf,
  dienVoorstelIn,
  type TrajectProject,
} from '../services/projectenApi';

/**
 * Mijn Projectvoorstellen — de professional-kant van het traject:
 * gerichte aanvragen accepteren (stap 2), de kennismaking terugzien (3),
 * het mijlpaalvoorstel uitwerken en indienen (4) en de Model-OvO
 * tegentekenen met SMS-2FA (5).
 */

const euro = (w: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(w || 0);

interface VoorstelRij {
  titel: string;
  budget_uren: string;
  uurtarief: string;
}

const VoorstelFormulier: React.FC<{ project: TrajectProject; onKlaar: () => void }> = ({ project, onKlaar }) => {
  const [rijen, setRijen] = React.useState<VoorstelRij[]>(
    project.milestones.length > 0
      ? project.milestones.map((m) => ({
          titel: m.titel,
          budget_uren: m.budget_uren ? String(parseFloat(m.budget_uren)) : '',
          uurtarief: m.uurtarief ? String(parseFloat(m.uurtarief)) : '',
        }))
      : [{ titel: '', budget_uren: '', uurtarief: '' }]
  );

  const mutation = useMutation({
    mutationFn: () =>
      dienVoorstelIn(
        project.id,
        rijen.map((r) => ({
          titel: r.titel.trim(),
          budget_uren: parseFloat(r.budget_uren),
          uurtarief: parseFloat(r.uurtarief),
        }))
      ),
    onSuccess: () => { toast.success('Voorstel verzonden naar de opdrachtgever'); onKlaar(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Indienen mislukt'),
  });

  const wijzig = (index: number, veld: keyof VoorstelRij, waarde: string) =>
    setRijen((oud) => oud.map((r, i) => (i === index ? { ...r, [veld]: waarde } : r)));

  const geldig = rijen.length > 0 && rijen.every(
    (r) => r.titel.trim().length >= 3 && parseFloat(r.budget_uren) > 0 && parseFloat(r.uurtarief) > 0
  );
  const totaal = rijen.reduce((som, r) => som + (parseFloat(r.budget_uren) || 0) * (parseFloat(r.uurtarief) || 0), 0);

  return (
    <div className="mt-4 border-t pt-4">
      <h4 className="font-medium text-gray-900 mb-1">Werk voorstel uit</h4>
      <p className="text-sm text-gray-600 mb-3">
        Knip de offerte op in concrete, resultaatgerichte mijlpalen met een urenbudget per mijlpaal.
      </p>
      {rijen.map((r, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_110px_110px_40px] gap-2 mb-2 items-end">
          <Input label={`Milestone ${i + 1}`} placeholder="Bijv. Uitvoeren fysieke veiligheidsscans op de werkvloer"
            value={r.titel} onChange={(e) => wijzig(i, 'titel', e.target.value)} />
          <Input label="Uren" type="number" min="0.5" step="0.5" value={r.budget_uren}
            onChange={(e) => wijzig(i, 'budget_uren', e.target.value)} />
          <Input label="Uurtarief" type="number" min="38" step="1" value={r.uurtarief}
            onChange={(e) => wijzig(i, 'uurtarief', e.target.value)} />
          <button className="text-red-500 pb-2" title="Verwijder mijlpaal" disabled={rijen.length === 1}
            onClick={() => setRijen((oud) => oud.filter((_, j) => j !== i))}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <Button size="sm" variant="outline" onClick={() => setRijen((oud) => [...oud, { titel: '', budget_uren: '', uurtarief: '' }])}>
          <Plus className="w-4 h-4 mr-1" /> Mijlpaal toevoegen
        </Button>
        <span className="text-sm text-gray-700">Totaal voorstel: <strong>{euro(totaal)}</strong> excl. btw</span>
        <Button size="sm" disabled={!geldig} loading={mutation.isPending} onClick={() => mutation.mutate()}>
          <Send className="w-4 h-4 mr-1" /> Verzenden naar Opdrachtgever
        </Button>
      </div>
    </div>
  );
};

const ProfessionalProjectVoorstellenPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [openContract, setOpenContract] = React.useState<string | null>(null);

  const { data: projecten, isLoading } = useQuery({
    queryKey: ['professional-projecttraject'],
    queryFn: async () => (await fetchProfessionalProjecten()).data?.data as TrajectProject[],
  });

  const herlaad = () => queryClient.invalidateQueries({ queryKey: ['professional-projecttraject'] });

  const accepteerMutation = useMutation({
    mutationFn: (projectId: string) => accepteerAanvraag(projectId),
    onSuccess: () => { toast.success('Aanvraag geaccepteerd — de opdrachtgever kan nu een kennismaking plannen.'); herlaad(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Accepteren mislukt'),
  });

  const afwijzenMutation = useMutation({
    mutationFn: ({ id, reden }: { id: string; reden: string }) => wijsAanvraagAf(id, reden),
    onSuccess: () => { toast.success('Aanvraag afgewezen'); herlaad(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Afwijzen mislukt'),
  });

  if (isLoading) {
    return <div className="max-w-4xl mx-auto px-4 py-8"><p className="text-center text-gray-600">Laden…</p></div>;
  }

  const lijst = (projecten || []).filter((p) =>
    ['AANGEVRAAGD', 'GEACCEPTEERD', 'PROPOSAL_SUBMITTED', 'CONTRACT_PENDING'].includes(p.status)
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Mijn Projectvoorstellen</h1>
      <p className="text-gray-600 mb-6">
        Gerichte projectaanvragen, mijlpaalvoorstellen en de digitale Model-OvO-ondertekening.
      </p>

      {lijst.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <Briefcase className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <p className="text-gray-600">Geen openstaande projectaanvragen of voorstellen.</p>
          </div>
        </Card>
      )}

      <div className="space-y-4">
        {lijst.map((p) => {
          const komendeKennismaking = p.kennismakingen.find((k) => k.status === 'GEPLAND');
          return (
            <Card key={p.id}>
              <div className="p-6">
                <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                  <div>
                    <span className="font-mono text-xs text-gray-500">{p.project_code}</span>
                    <h3 className="font-semibold text-gray-900">{p.titel}</h3>
                    <p className="text-sm text-gray-600">{p.werkgever_naam || 'Opdrachtgever'} · {p.professional_type}</p>
                    {p.omschrijving && <p className="text-sm text-gray-600 mt-1">{p.omschrijving}</p>}
                  </div>
                </div>

                {p.status === 'AANGEVRAAGD' && (
                  <div className="rounded-md bg-purple-50 border border-purple-200 p-4">
                    <p className="text-sm text-purple-900 mb-3">
                      💼 <strong>Directe Projectaanvraag:</strong> {p.werkgever_naam || 'een opdrachtgever'} heeft u
                      specifiek geselecteerd voor dit project. Heeft u tijd?
                      {p.aanvraag_verloopt_op && (
                        <> Reageer vóór {new Date(p.aanvraag_verloopt_op).toLocaleString('nl-NL')}.</>
                      )}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" loading={accepteerMutation.isPending} onClick={() => accepteerMutation.mutate(p.id)}>
                        🙋 Ik accepteer de aanvraag & heb tijd
                      </Button>
                      <Button size="sm" variant="danger" loading={afwijzenMutation.isPending}
                        onClick={() => {
                          const reden = window.prompt('Reden van afwijzing:');
                          if (reden && reden.trim().length >= 3) afwijzenMutation.mutate({ id: p.id, reden: reden.trim() });
                        }}>
                        Afwijzen
                      </Button>
                    </div>
                  </div>
                )}

                {komendeKennismaking && (
                  <p className="mt-3 text-sm text-gray-700 inline-flex items-center gap-2">
                    <Video className="w-4 h-4 text-purple-600" />
                    Video-kennismaking {new Date(komendeKennismaking.start_tijd).toLocaleString('nl-NL')} ({komendeKennismaking.duur_minuten} min) —{' '}
                    <a className="text-purple-700 underline" href={komendeKennismaking.video_url} target="_blank" rel="noreferrer">
                      open videolink
                    </a>
                  </p>
                )}

                {(p.status === 'GEACCEPTEERD' || p.status === 'PROPOSAL_SUBMITTED') && (
                  <>
                    {p.status === 'PROPOSAL_SUBMITTED' && (
                      <p className="mt-3 text-sm text-green-700">
                        Voorstel ingediend — de opdrachtgever beoordeelt het. U kunt het hieronder nog aanpassen en
                        opnieuw verzenden zolang er geen akkoord is.
                      </p>
                    )}
                    <VoorstelFormulier project={p} onKlaar={herlaad} />
                  </>
                )}

                {p.status === 'CONTRACT_PENDING' && p.contract && (
                  <div className="mt-4 border-t pt-3">
                    <p className="text-sm text-gray-700 mb-2">
                      ✍️ De opdrachtgever is akkoord. De Model-OvO (met uw mijlpalen als Bijlage 1) staat klaar.
                      {p.contract.status === 'WACHT_OP_PROFESSIONAL'
                        ? ' De opdrachtgever heeft getekend — nu uw tegentekening via SMS-verificatie.'
                        : ' Zodra de opdrachtgever heeft getekend, ontvangt u een SMS voor de tegentekening.'}
                    </p>
                    <Button size="sm" variant="outline" onClick={() => setOpenContract(openContract === p.id ? null : p.id)}>
                      {openContract === p.id ? 'Verberg contract' : 'Bekijk & onderteken Model-OvO'}
                    </Button>
                    {openContract === p.id && (
                      <div className="mt-3">
                        <OvoContractSign contractId={p.contract.id} rol="professional" onGetekend={herlaad} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ProfessionalProjectVoorstellenPage;
